/**
 * The Prometheus status chips, and how they translate to and from the REAL `matchStatus` column
 * filter.
 *
 * The chips do not own a filter of their own. They are a second face on ag-Grid's `matchStatus`
 * column filter: clicking a chip writes that column's filter model, and editing the column's header
 * filter directly syncs the chips back. There is therefore exactly ONE filter object for the field,
 * which is what keeps the toolbar, the column header, the Filters modal and the /query request body
 * from ever disagreeing. The previous shape — a separate `StatusFilterState` carried in an external
 * filter model and translated into the request — could and did drift from the grid's own state.
 *
 * ## The Ignored chip
 *
 * `IGNORED` is a real `matchStatus` value once the catalog's `matchStatus` entry lists it under
 * `allowedValues` (PVAL-26415) — the chip is enabled dynamically in `workbench-grid.component.ts`
 * once that's true, and stays disabled (always `false`, contributing nothing) against a catalog that
 * doesn't. No value is fabricated here either way: an unsupported catalog simply never sets `ignored`
 * true, so the predicate this module builds never names a value the backend doesn't recognise.
 */

/** The catalog field the status chips filter on, post-rename (was `status`). */
export const MATCH_STATUS_FIELD = 'matchStatus'

/** The chips. See the note on the Ignored chip above — it may be a no-op depending on the catalog. */
export interface MatchStatusChipState {
  matched: boolean
  unmatched: boolean
  ignored: boolean
}

/** ag-Grid's set-filter model, which is also what the /query contract's set entry is built from. */
export interface MatchStatusSetFilterModel {
  filterType: 'set'
  values: string[]
}

/**
 * The part of a live `matchStatus` filter model this module reads back.
 *
 * Separate from {@link MatchStatusSetFilterModel} — that is what we WRITE, this is what we may find
 * already applied. The read side has to be more permissive: `values` can legitimately be absent (a
 * cleared filter), and a set filter's values are `(string | null)[]` because ag-Grid represents
 * "(Blanks)" as a null entry.
 */
export interface MatchStatusFilterValues {
  values?: (string | null)[] | null
}

/**
 * Narrow whatever `getFilterInstance('matchStatus')?.getModel()` handed back onto
 * {@link MatchStatusFilterValues}.
 *
 * `getModel()` is typed `any` by ag-Grid, and the value genuinely varies: `null` for a cleared
 * filter, a set-filter model for ours, or some other filter's model shape entirely if the column's
 * `filter` is ever reconfigured. Asserting the set-filter shape with a cast — which is what both call
 * sites used to do — is unchecked in exactly the case that matters: a non-array `values` would reach
 * `values.includes(...)` and throw at runtime, on a code path (a catalog switch dropping Ignored
 * support) that is hard to reach in a test and easy to hit in production.
 *
 * An unreadable model is reported as "no value list", i.e. `{}`. That is the same reading an absent
 * `values` already gets — "this column is not constrained" — so the chips read as all-on and nothing
 * is silently treated as a restriction we cannot actually see. Deliberately total: this never throws,
 * because a malformed filter model must not be able to break the toolbar.
 */
export const readMatchStatusFilterModel = (
  model: unknown,
): MatchStatusFilterValues | null => {
  if (model == null || typeof model !== 'object') return null
  const values = (model as { values?: unknown }).values
  if (!Array.isArray(values)) return {}
  return {
    values: values.filter(
      (value): value is string | null =>
        value === null || typeof value === 'string',
    ),
  }
}

const CHIP_VALUE: Record<keyof MatchStatusChipState, string> = {
  unmatched: 'UNMATCHED',
  matched: 'MATCHED',
  ignored: 'IGNORED',
}

/** Chip order, so the emitted value list is stable and a request body is assertable. */
const CHIP_ORDER: ReadonlyArray<keyof MatchStatusChipState> = [
  'unmatched',
  'matched',
  'ignored',
]

/**
 * The `matchStatus` filter model for a chip state, or `null` when the chips must contribute NO filter
 * at all.
 *
 * `ignoredSupported` is whether the CATALOG lists `IGNORED` under `matchStatus.allowedValues` for the
 * account currently loaded — not whether the Ignored chip is merely rendered. When it is `false`,
 * `ignored` is forced to the neutral (non-restricting) reading regardless of what the caller passed,
 * on both ends:
 *  - for the null-check, an unsupported catalog must still reach "no filter" from Matched+Unmatched
 *    alone, exactly as it did before Ignored existed — an account with no ignore concept cannot be
 *    made to fail that check by a third chip it has no way to satisfy.
 *  - for the emitted values, `IGNORED` must never appear in the list when unsupported — enumerating a
 *    value the backend for this account does not recognise is the same "silently drops rows it hasn't
 *    enumerated" risk the whole `null`-over-enumeration design exists to avoid.
 *
 * `null` when every functional chip is on is the load-bearing case and is not an optimisation.
 * Enumerating `[MATCHED, UNMATCHED, IGNORED]` instead would silently drop every row whose
 * `matchStatus` is absent, is a value this UI has not enumerated, or is one the backend adds later —
 * a row loss that looks exactly like "this account has no positions". `null` clears the column's
 * filter, so no `matchStatus` entry reaches the request, which is what "do not constrain this column"
 * means.
 *
 * An all-functional-chips-OFF state returns `values: []`, which constrains the column to nothing and
 * therefore returns no rows. That is deliberate, not an oversight: it is a real, reachable user state
 * and it means "show nothing".
 */
export const matchStatusFilterModel = (
  chips: MatchStatusChipState,
  ignoredSupported: boolean,
): MatchStatusSetFilterModel | null => {
  const effective: MatchStatusChipState = ignoredSupported
    ? chips
    : { ...chips, ignored: true }

  if (effective.matched && effective.unmatched && effective.ignored) return null

  return {
    filterType: 'set',
    values: CHIP_ORDER.filter(chip => chip !== 'ignored' || ignoredSupported)
      .filter(chip => effective[chip])
      .map(chip => CHIP_VALUE[chip]),
  }
}

/**
 * The chip state implied by a `matchStatus` filter model — the sync-back direction, for when the user
 * edits the column's header filter (or the Filters modal) instead of clicking a chip.
 *
 * No filter means nothing is constrained, so every chip reads as ON, Ignored included — that is the
 * same reading {@link matchStatusFilterModel} emits `null` for, so a chip state survives a round trip
 * through the filter model unchanged. The caller is responsible for not letting an unsupported
 * Ignored chip actually enable itself in the UI (see `workbench-grid.component.ts`); this function
 * just reports what the filter model says.
 */
export const chipStateFromFilterModel = (
  model: MatchStatusFilterValues | null | undefined,
): MatchStatusChipState => {
  const values = model?.values
  if (!values) return { matched: true, unmatched: true, ignored: true }
  return {
    matched: values.includes(CHIP_VALUE.matched),
    unmatched: values.includes(CHIP_VALUE.unmatched),
    ignored: values.includes(CHIP_VALUE.ignored),
  }
}
